// StepThreeScreen.jsx
import React, { useState } from "react";
import {
  Box,
  Stack,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  TableBody,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import { Loader } from "../../Components/Loader";
import { useAuth } from "../../Context/AuthContext";

/* =========================================================
   MAIN STEP 3 SCREEN  (all portions combined)
   ========================================================= */

const StepThreeScreen = ({
  NextStep,
  bookletFile,
  apiResponse,
  isLoading,
  error,
  BASE_URL, // ✅ added for feedback API call
}) => {
  const { sessionId } = useAuth();

  // ✅ Feedback state (thumb-down only triggers API)
  const [vote, setVote] = useState(null); // "up" | "down" | null
  const [openFeedback, setOpenFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarType, setSnackbarType] = useState("success");
  const [snackbarMsg, setSnackbarMsg] = useState("Feedback submitted");

  const showSnack = (type, msg) => {
    setSnackbarType(type);
    setSnackbarMsg(msg);
    setSnackbarOpen(true);
  };

  const handleThumbUp = () => {
    setVote("up");
    setOpenFeedback(true);
  //  showSnack("success", "Thanks!");
  };

  const handleThumbDown = () => {
    setVote("down");
    setOpenFeedback(true);
  };

  const closeFeedbackDialog = () => {
    setOpenFeedback(false);
    setFeedbackText("");
  };

  const sendCarrierFeedback = async (text) => {
    if (!BASE_URL) throw new Error("BASE_URL not provided");
    if (!sessionId) throw new Error("sessionId not available");

    const payload = {
      action: "feedback",
      session_id: sessionId,
      thumb: vote,
      feedback: text,
      file_name: bookletFile.name,
    };

    const res = await fetch(`${BASE_URL}pfg-bp-carrier-translation`, {
      method: "POST",
      headers: { "Content-Type": "application/json","x-file-name":bookletFile.name },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errText = await res.text().catch(() => "");
      throw new Error(`Carrier feedback failed (${res.status}): ${errText}`);
    }
  };

  const handleSubmitFeedback = async () => {
    const text = feedbackText.trim();
    if (vote === "down" &&!text) {
      showSnack("warning", "Please enter feedback before submitting.");
      return;
    }

    try {
      setSubmitting(true);
      await sendCarrierFeedback(text);
      closeFeedbackDialog();
      showSnack("success", "Feedback submitted successfully");
    } catch (e) {
      console.error(e);
      showSnack("error", "Could not submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const getSubtitle = () => {
    if (apiResponse?.product_type?.includes("life")) {
      return "Basic Life and AD&D Plan Summary";
    }
    return "Basic Dental Plan Summary";
  };

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        py: 3,
        px: 2,
        display: "flex",
        justifyContent: "center",
      }}
    >
      {isLoading || !apiResponse ? (
        <Loader text={"Processing the document..."} error={error} />
      ) : (
        <Box sx={{ width: "95vw", maxWidth: 1500 }}>
          <Stack spacing={3}>
            {/* P1: Document Viewer + Upload Card */}
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={3}
                  alignItems="stretch"
                >
                  {/* Left – booklet view (fixed height, internal scroll) */}
                  <Box
                    sx={{
                      flex: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      overflow: "hidden",
                      minHeight: 560,
                    }}
                  >
                    <Box
                      sx={{
                        height: 560,
                        overflowY: "auto",
                        p: 3,
                        bgcolor: "background.paper",
                        fontFamily: "serif",
                      }}
                    >
                      <Box position="relative" width="100%">
                        <Typography
                          align="center"
                          sx={{
                            flexGrow: 1,
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            color: "primary.main",
                            mb: 1,
                          }}
                        >
                          {apiResponse?.fields["Insurance Company Name"]?.Value}
                        </Typography>
                        <Typography
                          sx={{
                            position: "absolute",
                            right: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                          }}
                        >
                          Document Confidence :{" "}
                          {(apiResponse?.document_confidence * 100).toFixed(0)}%
                        </Typography>
                      </Box>

                      <Typography
                        align="center"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        {getSubtitle()}
                      </Typography>
                      <Typography
                        align="center"
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                      >
                       {apiResponse?.fields["Effective Date"]?.Value ? `Effective ${apiResponse?.fields["Effective Date"]?.Value}` : ""}
                      </Typography>

                      <Divider sx={{ mb: 3 }} />

                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: "150px" }}>
                                Core Benefits
                              </TableCell>
                              <TableCell>Value</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell>Field Confidence</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.keys(apiResponse?.fields || {})?.map(
                              (key, index) => {
                                if (index !== 0 && index !== 1) {
                                  return (
                                    <TableRow key={index}>
                                      <TableCell sx={{ width: "150px" }}>
                                        {key}
                                      </TableCell>
                                      <TableCell>
                                        {apiResponse?.fields[key]?.Value}
                                      </TableCell>
                                      <TableCell>
                                        {apiResponse?.fields[key]?.Description}
                                      </TableCell>
                                      <TableCell>
                                        {(
                                          apiResponse?.fields[key]
                                            ?.final_confidence * 100
                                        ).toFixed(0)}
                                        %
                                      </TableCell>
                                    </TableRow>
                                  );
                                }
                                return null;
                              }
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* ✅ Feedback controls (thumb-down only triggers API) */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: 1,
                          mt: 2,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Was this extraction helpful?
                        </Typography>

                        <Tooltip title="Good Response">
                          <IconButton
                            onClick={handleThumbUp}
                            sx={{
                              border: "2px solid",
                              borderColor:
                                vote === "up" ? "success.main" : "divider",
                              bgcolor: "background.paper",
                            }}
                          >
                            <ThumbUpAltOutlinedIcon
                              sx={{ color: "success.main", fontSize: 20 }}
                            />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Bad Response (sends feedback)">
                          <IconButton
                            onClick={handleThumbDown}
                            sx={{
                              border: "2px solid",
                              borderColor:
                                vote === "down" ? "error.main" : "divider",
                              bgcolor: "background.paper",
                            }}
                          >
                            <ThumbDownAltOutlinedIcon
                              sx={{ color: "error.main", fontSize: 20 }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>

                  {/* Right – upload card */}
                  <Box
                    sx={{
                      flex: 1,
                      borderRadius: 2,
                      bgcolor: "action.hover",
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <DescriptionIcon
                      sx={{
                        fontSize: 48,
                        color: "primary.main",
                        mb: 2,
                      }}
                    />

                    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                      {bookletFile
                        ? bookletFile.name
                        : "Guardian_Basic_Life_Plan_2026.pdf"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      {"Uploaded on " + new Date().toLocaleDateString()}
                    </Typography>

                    <Box
                      sx={{
                        mt: 1,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: "rgba(46, 125, 50, 0.08)",
                        border: "1px solid",
                        borderColor: "success.main",
                        width: "100%",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "success.main", fontWeight: 500 }}
                      >
                        ✓ Document processed successfully
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Box sx={{ mt: 2, display: "flex" }}>
              <Button
                onClick={() => {
                  NextStep();
                }}
                variant="contained"
                size="small"
                color="primary"
                sx={{ textTransform: "none", px: 3, minWidth: 0, ml: "auto" }}
              >
                Proceed to Quote
              </Button>
            </Box>
          </Stack>
        </Box>
      )}

      {/* Feedback Dialog (thumb-down only) */}
      <Dialog
        open={openFeedback}
        onClose={closeFeedbackDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Share feedback</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            placeholder="I dont want the Insurance Company Name Field, can you remove it from the final output."
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeFeedbackDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitFeedback}
            disabled={submitting}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarType}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StepThreeScreen;
